import { useMemo, useState, type ReactNode } from "react";

import { formatDateShort, formatEuro, formatQty, toNumber } from "../lib/format";
import FilterDropdown from "./FilterDropdown";
import { GROUP_LABELS, groupRows, groupSummary, type GroupKey, type HistoryExportGroup } from "./historyGroups";
import HistoryGroupHeading from "./HistoryGroupHeading";
import SignedDeviation from "./SignedDeviation";

// The one history table (#544). Every view picks columns from this catalog but
// never changes their order, the sorting or how a value is shown. Newest first,
// cost center always present, one row per position. Below `md` each row is a
// card so nothing has to be scrolled sideways on a phone.

export const HISTORY_COLUMNS = [
  "date",
  "kind",
  "document",
  "article",
  "description",
  "costCenter",
  "supplier",
  "qty",
  "orderedPrice",
  "unitPrice",
  "reference",
  "deviation",
  "deviationTotal",
  "total",
  "status",
] as const;

export type HistoryColumnId = (typeof HISTORY_COLUMNS)[number];

type SortKey = "date" | "qty" | "unitPrice" | "total";

export type HistoryRow<T> = {
  id: string;
  data: T;
  date: string | null;
  kind?: ReactNode;
  kindTitle?: string;
  document?: ReactNode;
  article?: ReactNode;
  description?: string | null;
  reference?: ReactNode;
  status?: ReactNode;
  current?: boolean;
  costCenter?: ReactNode;
  supplier?: ReactNode;
  qty?: string | number | null;
  uom?: string | null;
  orderedPrice?: string | number | null;
  unitPrice?: string | number | null;
  unitPriceNote?: ReactNode;
  deviation?: { amount: string | number | null; percent: string | number | null } | null;
  deviationTotal?: string | number | null;
  total?: string | number | null;
  /** Highlights the best price of the shown period, like the price history did. */
  highlight?: "best" | "worst" | "muted";
  /** Everything the search box looks at; lower case. */
  searchText?: string;
  /** Plain labels used for grouping and for merging equal rows (#545). */
  groupLabels?: { costCenter?: string | null; supplier?: string | null; document?: string | null };
  /** Set when several equal rows were merged into this one (#545). */
  mergedCount?: number;
  originalRows?: readonly HistoryRow<T>[];
  /** Explicit adapter evidence; never infer comparability from a rendered label. */
  priceComparable?: boolean;
  comparisonKey?: string;
  referenceUom?: string | null;
  mergeKey?: string;
  groupIds?: { costCenter?: string; supplier?: string; document?: string };
  /** Original evidence behind a merged display row; exports must preserve every position. */
  originalData?: readonly T[];
  mergedFrom?: string | null;
  mergedTo?: string | null;
};

function MergedValue({ count, noun }: { count?: number; noun: string }) {
  return <span className="text-sm text-slate-500">{count ? `${count} ${noun}` : `mehrere ${noun}`}</span>;
}

/** Consecutive rows of the same wholesaler and price become one line (#545). */
function mergeEqualRows<T>(rows: HistoryRow<T>[]): HistoryRow<T>[] {
  const merged: HistoryRow<T>[] = [];
  for (const row of rows) {
    const previous = merged.at(-1);
    const sameSupplier = (previous?.groupLabels?.supplier ?? null) === (row.groupLabels?.supplier ?? null);
    const samePrice = toNumber(previous?.unitPrice ?? null) === toNumber(row.unitPrice ?? null);
    const samePriceKnown = toNumber(row.unitPrice ?? null) !== null;
    const sameUom = (previous?.uom ?? null) === (row.uom ?? null);
    if (!previous || !sameSupplier || !samePrice || !samePriceKnown || !sameUom || previous.mergeKey !== row.mergeKey) {
      merged.push({ ...row, originalData: row.originalData ?? [row.data], originalRows: row.originalRows ?? [row], mergedCount: 1, mergedFrom: row.date, mergedTo: row.date });
      continue;
    }
    const sameCostCenter = (previous.groupLabels?.costCenter ?? null) === (row.groupLabels?.costCenter ?? null);
    merged[merged.length - 1] = {
      ...previous,
      qty: addKnown(previous.qty, row.qty),
      total: addKnown(previous.total, row.total),
      mergedCount: (previous.mergedCount ?? 1) + 1,
      originalData: [...(previous.originalData ?? [previous.data]), ...(row.originalData ?? [row.data])],
      originalRows: [...(previous.originalRows ?? [previous]), ...(row.originalRows ?? [row])],
      // Rows arrive in the current sort order; keep the outer dates.
      mergedFrom: row.date && previous.mergedFrom && row.date < previous.mergedFrom ? row.date : previous.mergedFrom,
      mergedTo: row.date && previous.mergedTo && row.date > previous.mergedTo ? row.date : previous.mergedTo,
      // A merged line covers several documents, so it names none of them; the
      // cost center only stays when every merged position shares it.
      document: <MergedValue count={(previous.mergedCount ?? 1) + 1} noun="Belege" />,
      costCenter: sameCostCenter ? previous.costCenter : <MergedValue noun="Kostenstellen" />,
      groupLabels: sameCostCenter ? previous.groupLabels : { ...previous.groupLabels, costCenter: null },
      highlight: previous.highlight ?? row.highlight,
    };
  }
  return merged;
}

function addKnown(a: string | number | null | undefined, b: string | number | null | undefined) {
  const left = toNumber(a ?? null), right = toNumber(b ?? null);
  return left === null || right === null ? null : left + right;
}

const LABELS: Record<HistoryColumnId, string> = {
  date: "Datum",
  kind: "Typ",
  document: "Beleg",
  article: "Artikel",
  description: "Bezeichnung",
  reference: "Historische Referenz",
  deviationTotal: "Abweichung gesamt",
  status: "Status",
  costCenter: "Kostenstelle",
  supplier: "Großhändler",
  qty: "Menge",
  orderedPrice: "Bestellt",
  unitPrice: "Netto-Preis",
  deviation: "Abweichung",
  total: "Zeilensumme",
};

const ALIGN_RIGHT: Partial<Record<HistoryColumnId, boolean>> = { qty: true, orderedPrice: true, unitPrice: true, reference: true, deviation: true, deviationTotal: true, total: true };
const SORTABLE: Partial<Record<HistoryColumnId, SortKey>> = { date: "date", qty: "qty", unitPrice: "unitPrice", total: "total" };
// Narrow desktops drop the two columns the price history has always dropped;
// on a phone the card shows every value anyway.
const WIDTHS: Partial<Record<HistoryColumnId, string>> = {
  date: "w-24",
  kind: "w-24",
  document: "w-40",
  costCenter: "w-40",
  supplier: "w-32",
  qty: "w-24",
  orderedPrice: "w-28",
  unitPrice: "w-28",
  reference: "w-40",
  deviation: "w-32",
  deviationTotal: "w-32",
  status: "w-36",
  total: "w-28",
};

function sortValue<T>(row: HistoryRow<T>, key: SortKey): string | number | null {
  switch (key) {
    case "date":
      return row.date;
    case "qty":
      return toNumber(row.qty ?? null);
    case "unitPrice":
      return toNumber(row.unitPrice ?? null);
    case "total":
      return toNumber(row.total ?? null);
  }
}

function cell<T>(row: HistoryRow<T>, column: HistoryColumnId, deviationLayout: "stacked" | "inline"): ReactNode {
  switch (column) {
    case "date":
      return row.mergedCount && row.mergedCount > 1 ? (
        <span className="tabular-nums">
          {formatDateShort(row.mergedTo)}
          <span className="block text-xs text-slate-500">bis {formatDateShort(row.mergedFrom)}</span>
        </span>
      ) : (
        <span className="tabular-nums">
          {formatDateShort(row.date)}
          {row.current ? <span className="mt-1 block text-xs font-medium text-emerald-800">Aktuell geprüft</span> : null}
        </span>
      );
    case "orderedPrice":
      return row.orderedPrice === null || row.orderedPrice === undefined ? <span className="text-slate-400">–</span> : <span className="tabular-nums">{formatEuro(row.orderedPrice)}</span>;
    case "kind":
      return row.kind ?? null;
    case "document":
      return row.document ?? <span className="text-slate-400">–</span>;
    case "article":
      return row.article ?? "–";
    case "reference":
      return row.reference ?? "–";
    case "status":
      return row.status ?? "–";
    case "deviationTotal":
      return <SignedDeviation amount={row.deviationTotal} />;
    case "description":
      return <span className="text-slate-700">{row.description ?? "–"}</span>;
    case "costCenter":
      return row.costCenter ?? <span className="text-slate-400">–</span>;
    case "supplier":
      return row.supplier ?? <span className="text-slate-400">–</span>;
    case "qty":
      return (
        <span className="tabular-nums">
          {formatQty(row.qty ?? null)}
          {row.uom ? <span className="ml-1 text-xs text-slate-500">{row.uom}</span> : null}
        </span>
      );
    case "unitPrice":
      return (
        <span className="tabular-nums font-medium">
          {formatEuro(row.unitPrice ?? null)}
          {row.unitPriceNote ? <span className="mt-1 block">{row.unitPriceNote}</span> : null}
        </span>
      );
    case "deviation":
      return row.deviation ? (
        <SignedDeviation layout={deviationLayout} amount={row.deviation.amount} percent={row.deviation.percent} />
      ) : (
        <span className="text-slate-400">–</span>
      );
    case "total":
      return <span className="tabular-nums text-slate-600">{formatEuro(row.total ?? null)}</span>;
  }
}

export default function HistoryTable<T>({
  rows,
  columns,
  searchPlaceholder = "Bezeichnung, Kostenstelle, Beleg …",
  filters,
  onExport,
  exportLabel = "Export für Excel",
  emptyText = "Keine Historienzeilen vorhanden.",
  filteredEmptyText = "Keine Einträge für den gewählten Filter.",
  minWidthClass = "min-w-[900px]",
  groupable = false,
  showPriceMetrics = true,
  groupOptions = ["costCenter", "supplier", "document", "month", "price"],
  grouping,
  rememberGrouping,
  pageSize,
  testId,
  clientControls = true,
  labels,
  columnWidths,
  hideBelowXl = ["supplier", "total"],
  deviationLayout = "stacked",
}: {
  rows: HistoryRow<T>[];
  columns: readonly HistoryColumnId[];
  searchPlaceholder?: string;
  filters?: ReactNode;
  onExport?: (rows: readonly T[], groups?: readonly HistoryExportGroup<T>[]) => void;
  exportLabel?: string;
  emptyText?: string;
  filteredEmptyText?: string;
  minWidthClass?: string;
  /** Offers grouping and merging of equal prices (#545). */
  groupable?: boolean;
  /** Suppress price metrics when the source only provides document totals. */
  showPriceMetrics?: boolean;
  groupOptions?: readonly GroupKey[];
  grouping?: { groupBy: GroupKey | null; mergeEqual: boolean; onChange: (groupBy: GroupKey | null, mergeEqual: boolean) => void };
  rememberGrouping?: ReactNode;
  /** Zeigt zuerst nur so viele Zeilen; der Rest folgt auf Wunsch (#544). */
  pageSize?: number;
  /** Anker für Tests, unabhängig vom Layout. */
  testId?: string;
  /** Server-paged sources keep their global order; no misleading page-only search/sort. */
  clientControls?: boolean;
  labels?: Partial<Record<HistoryColumnId, string>>;
  columnWidths?: Partial<Record<HistoryColumnId, string>>;
  hideBelowXl?: readonly HistoryColumnId[];
  deviationLayout?: "stacked" | "inline";
}) {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({ key: "date", dir: "desc" });
  const [localGroupBy, setLocalGroupBy] = useState<GroupKey | null>(null);
  const groupBy = grouping ? grouping.groupBy : localGroupBy;
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [localMergeEqual, setLocalMergeEqual] = useState(false);
  const mergeEqual = grouping?.mergeEqual ?? localMergeEqual;
  const [exportGroups, setExportGroups] = useState(false);
  function setGroupBy(value: GroupKey | null) {
    setLocalGroupBy(value); grouping?.onChange(value, mergeEqual); setCollapsed(new Set());
  }
  function setMergeEqual(value: boolean) {
    setLocalMergeEqual(value); grouping?.onChange(groupBy, value);
  }
  const [shownCount, setShownCount] = useState(pageSize ?? Number.POSITIVE_INFINITY);
  const shown = useMemo(() => {
    if (!clientControls) return rows;
    const needle = search.trim().toLowerCase();
    const matching = needle ? rows.filter((row) => (row.searchText ?? "").includes(needle)) : rows;
    // Absteigend wird gerechnet, nicht umgedreht: ein umgedrehtes Ergebnis
    // würde auch gleichrangige Zeilen vertauschen, etwa alle Positionen
    // desselben Tages. So bleibt ihre übergebene Reihenfolge erhalten.
    return [...matching].sort((a, b) => {
      const left = sortValue(a, sort.key);
      const right = sortValue(b, sort.key);
      const missing = left === null || left === undefined;
      const otherMissing = right === null || right === undefined;
      if (missing || otherMissing) return missing ? (otherMissing ? 0 : 1) : -1;
      const ascending =
        typeof left === "number" && typeof right === "number"
          ? left - right
          : String(left).localeCompare(String(right));
      return sort.dir === "desc" ? -ascending : ascending;
    });
  }, [rows, search, sort, clientControls]);

  const groups = useMemo(() => {
    const grouped = groupRows(shown, groupBy);
    return mergeEqual ? grouped.map((group) => ({ ...group, rows: mergeEqualRows(group.rows) })) : grouped;
  }, [shown, groupBy, mergeEqual]);
  const allVisibleRows = groups.flatMap((group) => (collapsed.has(group.key) ? [] : group.rows));
  // Lange Historien öffnen kurz; der Rest folgt auf Wunsch, ohne Seitensprung.
  // Das Budget läuft über die Gruppen hinweg, damit Gruppenköpfe erhalten
  // bleiben und eingeklappte Gruppen nichts davon verbrauchen.
  const pagedGroups = useMemo(() => {
    if (!Number.isFinite(shownCount)) return groups;
    let budget = shownCount;
    const limited: typeof groups = [];
    for (const group of groups) {
      if (collapsed.has(group.key)) { limited.push(group); continue; }
      if (budget <= 0) break;
      const rows = group.rows.slice(0, budget);
      budget -= rows.length;
      limited.push({ ...group, rows });
    }
    return limited;
  }, [groups, shownCount, collapsed]);
  const visibleRows = pagedGroups.flatMap((group) => (collapsed.has(group.key) ? [] : group.rows));
  const hiddenCount = allVisibleRows.length - visibleRows.length;

  const columnLabels = { ...LABELS, ...labels };

  // Keep the catalog order whatever the caller passes.
  const ordered = HISTORY_COLUMNS.filter((column) => columns.includes(column));

  const exportAction = onExport ? groupable ? (
    <details className="relative ml-auto" onKeyDown={event => { if (event.key === "Escape") event.currentTarget.open = false; }}>
      <summary role="button" aria-label={exportLabel} className="flex h-9 cursor-pointer list-none items-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm">{exportLabel} <span aria-hidden="true">▾</span></summary>
      <div className="absolute right-0 z-30 mt-1 w-72 max-w-[calc(100vw-3rem)] rounded-md border border-slate-200 bg-white p-3 text-sm shadow-lg">
        <label className="flex items-start gap-2 text-slate-700">
          <input type="checkbox" className="mt-1 accent-emerald-600" disabled={!groupBy} checked={exportGroups && !!groupBy} onChange={event => setExportGroups(event.target.checked)} />
          Gruppenüberschriften und Summen ergänzen
        </label>
        <p className="my-2 text-xs text-slate-500">Exportiert alle Suchtreffer, auch noch nicht eingeblendete Zeilen. Eingeklappte Gruppen sind ausgenommen; zusammengefasste Zeilen enthalten alle Originalpositionen.</p>
        <button type="button" disabled={!allVisibleRows.length} className="h-9 font-medium text-emerald-800 disabled:opacity-50" onClick={event => {
          const selected = groups.filter(group => !collapsed.has(group.key) && group.rows.length);
          onExport(allVisibleRows.flatMap(row => row.originalData ?? [row.data]), exportGroups && groupBy ? selected.map(group => ({
            label: group.label, rows: group.rows.flatMap(row => row.originalData ?? [row.data]), summary: groupSummary(group.rows),
          })) : undefined);
          const details = event.currentTarget.closest("details"); if (details) details.open = false;
        }}>CSV herunterladen</button>
      </div>
    </details>
  ) : (
    <button type="button" onClick={() => onExport(allVisibleRows.flatMap(row => row.originalData ?? [row.data]))} disabled={!allVisibleRows.length}
      className="ml-auto h-9 rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm disabled:opacity-50">{exportLabel}</button>
  ) : null;

  if (rows.length === 0) {
    return <div className="rounded-lg border border-dashed border-slate-300 bg-white px-4 py-6 text-sm text-slate-500">{emptyText}</div>;
  }

  return (
    <div>
      {clientControls ? <div className="mb-2 flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={searchPlaceholder}
          aria-label="Historie durchsuchen"
          className="h-9 w-full min-w-0 sm:w-72 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700 placeholder-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
        />
        {filters}
        {groupable ? (
          <>
            <FilterDropdown<GroupKey | "">
              label="Gruppierung" ariaLabel="Tabelle gruppieren" value={groupBy ?? ""} defaultValue=""
              options={[{ value: "", label: "Ohne Gruppierung" }, ...groupOptions.map(value => ({ value, label: GROUP_LABELS[value] }))]}
              onChange={value => setGroupBy(value || null)}
            />
            <label className="flex cursor-pointer items-center gap-1.5 text-xs text-slate-600" title="Aufeinanderfolgende Zeilen mit gleichem Großhändler und gleichem Preis werden zu einer Zeile mit Zeitraum und Gesamtmenge.">
              <input
                type="checkbox"
                checked={mergeEqual}
                onChange={(event) => setMergeEqual(event.target.checked)}
                className="accent-emerald-600"
              />
              Gleiche Preise zusammenfassen
            </label>
          </>
        ) : null}
        <span className="text-xs text-slate-400">
          {mergeEqual ? `${visibleRows.length} Zeilen aus ${shown.length} Positionen` : `${shown.length} von ${rows.length} Zeilen`}
        </span>
        {exportAction}
      </div> : filters || (onExport && !groupable) ? <div className="mb-2 flex flex-wrap items-center gap-3">
        {filters}
        {exportAction}
      </div> : null}
      {groupable ? <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-600">
        {groupBy ? <>
          <button type="button" onClick={() => setCollapsed(new Set())} className="min-h-9 hover:text-emerald-800">Alle aufklappen</button>
          <button type="button" onClick={() => setCollapsed(new Set(groups.map(group => group.key)))} className="min-h-9 hover:text-emerald-800">Alle zuklappen</button>
        </> : null}
        {rememberGrouping ? <div className="ml-auto">{rememberGrouping}</div> : null}
        {!clientControls ? exportAction : null}
      </div> : null}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="md:hidden divide-y divide-slate-100">
          {shown.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-slate-400">{filteredEmptyText}</p>
          ) : (
            pagedGroups.flatMap((group) => [
              ...(groupBy
                ? [
                    <GroupHeadCard
                      key={`group-${group.key}`}
                      name={group.label}
                      rows={group.sourceRows}
                      showPriceMetrics={showPriceMetrics}
                      collapsed={collapsed.has(group.key)}
                      onToggle={() =>
                        setCollapsed((previous) => {
                          const next = new Set(previous);
                          if (next.has(group.key)) next.delete(group.key);
                          else next.add(group.key);
                          return next;
                        })
                      }
                    />,
                  ]
                : []),
              ...(collapsed.has(group.key) ? [] : group.rows).map((row) => (
                <HistoryCard key={row.id} row={row} columns={ordered} labels={columnLabels} deviationLayout={deviationLayout} />
              )),
            ])
          )}
        </div>
        <div className="hidden overflow-x-auto md:block">
          <table data-testid={testId} className={`w-full ${minWidthClass} table-fixed text-sm lg:min-w-0`}>
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                {ordered.map((column) => (
                  <th
                    key={column}
                    style={columnWidths?.[column] ? { width: columnWidths[column] } : undefined}
                    className={`${columnWidths?.[column] ? "" : WIDTHS[column] ?? ""} overflow-hidden px-3 py-2 ${ALIGN_RIGHT[column] ? "text-right" : ""} ${hideBelowXl.includes(column) ? "hidden xl:table-cell" : ""}`}
                  >
                    {clientControls && SORTABLE[column] ? (
                      <SortButton
                        label={columnLabels[column]}
                        active={sort.key === SORTABLE[column]}
                        dir={sort.dir}
                        onClick={() =>
                          setSort((previous) =>
                            previous.key === SORTABLE[column]
                              ? { key: previous.key, dir: previous.dir === "asc" ? "desc" : "asc" }
                              : { key: SORTABLE[column]!, dir: "desc" },
                          )
                        }
                      />
                    ) : (
                      columnLabels[column]
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {shown.length === 0 ? (
                <tr>
                  <td colSpan={ordered.length} className="px-3 py-6 text-center text-sm text-slate-400">
                    {filteredEmptyText}
                  </td>
                </tr>
              ) : (
                pagedGroups.flatMap((group) => [
                  ...(groupBy
                    ? [
                        <GroupHeadRow
                          key={`group-${group.key}`}
                          name={group.label}
                          rows={group.sourceRows}
                          showPriceMetrics={showPriceMetrics}
                          columns={ordered.length}
                          collapsed={collapsed.has(group.key)}
                          onToggle={() =>
                            setCollapsed((previous) => {
                              const next = new Set(previous);
                              if (next.has(group.key)) next.delete(group.key);
                              else next.add(group.key);
                              return next;
                            })
                          }
                        />,
                      ]
                    : []),
                  ...(collapsed.has(group.key) ? [] : group.rows).map((row) => (
                  <tr key={row.id} aria-current={row.current ? "true" : undefined} data-history-row={row.id} className={rowClass(row)}>
                    {ordered.map((column) => (
                      <td
                        key={column}
                        data-history-cell={column}
                        className={`overflow-hidden px-3 py-2 align-top ${ALIGN_RIGHT[column] ? "text-right" : ""} ${hideBelowXl.includes(column) ? "hidden xl:table-cell" : ""} ${column === "description" ? "truncate" : "break-words"}`}
                        title={column === "description" ? row.description ?? undefined : column === "kind" ? row.kindTitle : undefined}
                      >
                        {cell(row, column, deviationLayout)}
                      </td>
                    ))}
                  </tr>
                  )),
                ])
              )}
            </tbody>
          </table>
        </div>
      </div>
      {hiddenCount > 0 ? (
        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
          <button
            type="button"
            onClick={() => setShownCount((current) => current + (pageSize ?? 10))}
            className="h-9 rounded-md border border-slate-300 bg-white px-3 font-medium text-slate-700 shadow-sm hover:bg-slate-50"
          >
            Weitere {Math.min(hiddenCount, pageSize ?? 10)} anzeigen
          </button>
          <span className="text-slate-600">
            {visibleRows.length} von {allVisibleRows.length} angezeigt
          </span>
        </div>
      ) : null}
    </div>
  );
}

type GroupHeadProps<T> = { name: string; rows: readonly HistoryRow<T>[]; collapsed: boolean; onToggle: () => void; showPriceMetrics?: boolean };
function GroupHeadRow<T>({ columns, ...props }: GroupHeadProps<T> & { columns: number }) {
  return <tr className="bg-slate-50"><td colSpan={columns} className="px-3 py-2"><HistoryGroupHeading {...props} /></td></tr>;
}
function GroupHeadCard<T>(props: GroupHeadProps<T>) {
  return <div className="bg-slate-50 px-4 py-2"><HistoryGroupHeading {...props} /></div>;
}

function rowClass<T>(row: HistoryRow<T>): string {
  if (row.current || row.highlight === "best") return "bg-emerald-50 hover:bg-emerald-100 [&>td:first-child]:shadow-[inset_3px_0_0_#28785e]";
  if (row.highlight === "worst") return "bg-rose-50 hover:bg-rose-100 [&>td:first-child]:shadow-[inset_3px_0_0_#be123c]";
  if (row.highlight === "muted") return "bg-amber-50/40 hover:bg-amber-50";
  return "hover:bg-slate-50";
}

function HistoryCard<T>({ row, columns, labels, deviationLayout }: { row: HistoryRow<T>; columns: readonly HistoryColumnId[]; labels: Record<HistoryColumnId, string>; deviationLayout: "stacked" | "inline" }) {
  const HEAD: readonly HistoryColumnId[] = ["date", "kind", "document"];
  const head = columns.filter((column) => HEAD.includes(column));
  const rest = columns.filter((column) => !HEAD.includes(column) && column !== "description" && column !== "article" && column !== "status");
  return (
    <div aria-current={row.current ? "true" : undefined} data-history-row={row.id} className={`break-words px-4 py-3 text-sm ${row.current || row.highlight === "best" ? "bg-emerald-50" : row.highlight === "worst" ? "bg-rose-50" : row.highlight === "muted" ? "bg-amber-50/40" : ""}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {head.map((column) => (
          <div key={column} data-history-cell={column}>{cell(row, column, deviationLayout)}</div>
        ))}
      </div>
      {columns.includes("article") || columns.includes("status") ? (
        <div className={`flex items-start gap-3 ${head.length ? "mt-2" : ""}`}>
          {columns.includes("article") ? <div data-history-cell="article" className="min-w-0 flex-1">{cell(row, "article", deviationLayout)}</div> : null}
          {columns.includes("status") ? <div data-history-cell="status" className="max-w-[45%]">{cell(row, "status", deviationLayout)}</div> : null}
        </div>
      ) : null}
      {columns.includes("description") ? (
        <p className="mt-1 font-medium text-slate-900">{row.description ?? "–"}</p>
      ) : null}
      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2">
        {rest.map((column) => (
          <div key={column} className={ALIGN_RIGHT[column] ? "text-right" : ""}>
            <dt className="text-xs text-slate-500">{labels[column]}</dt>
            <dd data-history-cell={column} className="mt-0.5">{cell(row, column, deviationLayout)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function SortButton({ label, active, dir, onClick }: { label: string; active: boolean; dir: "asc" | "desc"; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Nach ${label} sortieren`}
      className={`inline-flex items-center gap-1 whitespace-nowrap uppercase tracking-wide ${active ? "text-emerald-700" : "text-slate-500 hover:text-slate-700"}`}
    >
      {label}
      <span className="text-[10px] opacity-70">{active ? (dir === "asc" ? "▲" : "▼") : "↕"}</span>
    </button>
  );
}
